/**
 * PublicDrawResultsCard — contestant-facing per-performance draw display.
 *
 * Mounted on EventDetailPage. Renders ONLY when the admin has flipped
 * `draw_results_visible = true` on the event AND there's a finalized
 * (or published) draw to show — the backend handles the gating with a
 * 404 on either condition; the FE shows nothing in that case.
 *
 * Deliberately stripped down vs. the admin EventDraftSummary:
 *   - no priority/weight (admin-only)
 *   - no member/non-member badges
 *   - no draw-out / unplaced buckets (contestant doesn't need to see
 *     who got removed; if they were removed they know already)
 */

import { useState } from 'react';
import {
  AlertCircle,
  CalendarClock,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { usePublicDrawResults, type PublicDrawResults } from '@/api/extensions/useDrawOps';

interface Props {
  eventId: string;
  className?: string;
}

export type DrawEntry = PublicDrawResults['performances'][number]['entries'][number];

export interface Roper {
  name: string;
  position: string | null;
  isPrimary: boolean;
}

const isHeader = (pos: string | null) => (pos ?? '').trim().toLowerCase() === 'header';

/**
 * Build the ordered roper list for a draw entry. For team ropings this
 * returns the header first, then the heeler — regardless of which one is
 * the primary (entry owner) — so contestants always read the team in
 * roping order. The primary keeps its `isPrimary` flag so the UI can bold
 * it. Falls back to primary-first when positions are missing (incomplete
 * team / non-team-roping data).
 */
export function buildRopers(e: DrawEntry): Roper[] {
  const ropers: Roper[] = [
    { name: e.display_name ?? 'Unknown', position: e.team_position, isPrimary: true },
  ];
  if (e.is_team && e.team_partner_name) {
    ropers.push({
      name: e.team_partner_name,
      position: e.team_partner_position,
      isPrimary: false,
    });
  }
  // Header always first. Stable for ties (both null) → primary stays first.
  return ropers
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const rank = (x: Roper) => (isHeader(x.position) ? 0 : 1);
      const diff = rank(a.r) - rank(b.r);
      return diff !== 0 ? diff : a.i - b.i;
    })
    .map(({ r }) => r);
}

export function PublicDrawResultsCard({ eventId, className }: Props) {
  const { data, isLoading, isError } = usePublicDrawResults(eventId);
  const [expandedPerfs, setExpandedPerfs] = useState<Set<string>>(new Set());

  // 404 / not-visible / no-execution all collapse to "render nothing".
  // The card is opt-in: when results aren't published the contestant
  // shouldn't even see an empty placeholder taking up vertical space.
  if (isLoading) return null;
  if (isError || !data) return null;

  const toggle = (id: string) =>
    setExpandedPerfs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const total = data.performances.reduce((acc, p) => acc + p.entries.length, 0);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-2">
            Draw Results
            <Badge
              variant="default"
              className={
                data.placement_status === 'finalized'
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white text-[10px]'
                  : 'bg-green-600 hover:bg-green-700 text-white text-[10px]'
              }
            >
              {data.placement_status === 'finalized' ? 'Finalized' : 'Published'}
            </Badge>
          </span>
          <span className="text-xs font-normal text-muted-foreground">
            {total} {total === 1 ? 'entry' : 'entries'}
          </span>
        </CardTitle>
        {data.last_applied_at && (
          <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
            <CalendarClock className="h-3 w-3" />
            Updated:{' '}
            <time dateTime={data.last_applied_at}>
              {new Date(data.last_applied_at).toLocaleString()}
            </time>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-1.5">
        {data.performances.length === 0 ? (
          <div className="rounded border border-dashed bg-muted/20 p-3 text-xs text-muted-foreground flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5" />
            No performances configured.
          </div>
        ) : (
          data.performances.map((p) => {
            const open = expandedPerfs.has(p.performance_id);
            return (
              <div key={p.performance_id} className="rounded-md border bg-card">
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/30"
                  onClick={() => toggle(p.performance_id)}
                >
                  <span className="flex items-center gap-2 text-sm">
                    {open ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="font-medium">{p.performance_name}</span>
                    {p.is_slack && (
                      <Badge variant="outline" className="text-[10px]">
                        slack
                      </Badge>
                    )}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {p.entries.length} {p.entries.length === 1 ? 'entry' : 'entries'}
                  </span>
                </button>
                {open && p.entries.length > 0 && (
                  <ol className="border-t divide-y text-sm">
                    {p.entries.map((e) => {
                      const ropers = buildRopers(e);
                      return (
                        <li key={e.id} className="flex items-baseline gap-2 px-3 py-1.5">
                          <span className="text-muted-foreground tabular-nums min-w-[1.75rem]">
                            {e.draw_position}.
                          </span>
                          {e.is_team && ropers.length > 1 ? (
                            <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                              {ropers.map((r, i) => (
                                <span key={i} className="inline-flex items-baseline gap-1">
                                  {i > 0 && (
                                    <span className="text-muted-foreground">&amp;</span>
                                  )}
                                  {r.position && (
                                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                      {r.position}
                                    </span>
                                  )}
                                  <span className={r.isPrimary ? 'font-semibold' : ''}>
                                    {r.name}
                                  </span>
                                  {r.isPrimary && (
                                    <Badge
                                      variant="secondary"
                                      className="px-1 py-0 text-[9px] font-medium uppercase leading-tight tracking-wide"
                                      title="Primary entry holder — the roper who registered the team"
                                    >
                                      Primary
                                    </Badge>
                                  )}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <span>{e.display_name ?? 'Unknown'}</span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
                {open && p.entries.length === 0 && (
                  <div className="border-t px-3 py-2 text-xs text-muted-foreground italic">
                    No contestants drawn into this performance.
                  </div>
                )}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
